import { Component, Input, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-recorrido',
    standalone: true,
    imports: [CommonModule, HttpClientModule, MatDialogModule, MatButtonModule, MatIconModule],
    template: `
    <!-- Si no estamos en un modal, mostrar el botón -->
    <button
    mat-raised-button
      *ngIf="!enDialog"
       class="h-12 px-6 text-white bg-gray-600 hover:bg-gray-700 text-xs"
      (click)="abrirDialog()"
      [disabled]="!alias || !id_viaje">
      Ver recorrido
    </button>

    <!-- Si estamos en el modal, mostrar el contenido -->
    <div *ngIf="enDialog" class="max-w-xl max-h-[80vh] overflow-auto p-4">
    
    <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">Recorrido del Viaje</h2>
        <button mat-icon-button (click)="dialogRef.close()" aria-label="Cerrar">
            <mat-icon>close</mat-icon>
        </button>
    </div>
    <div *ngIf="loading" class="text-gray-500">Cargando recorrido...</div>
    <div *ngIf="error" class="text-red-600 text-center text-lg">La empresa no ha publicado el recorrido del viaje.</div>
    <div *ngIf="stops.length > 0" class="relative border-l-2 border-blue-200 pl-6">
        <div class="animate-fade-in transition-opacity duration-300">
        <div *ngFor="let stop of stops; let i = index" class="mb-8 grid grid-cols-[60px_1fr] gap-3 relative">
            <!-- Punto de la línea de tiempo -->
            <div class="absolute -left-[11px] top-1 w-4 h-4 rounded-full border-2"
                [ngClass]="getColor(i, stop.TerminalID)">
            </div>

            <!-- Hora -->
            <div class="text-sm font-medium text-gray-800 mt-1 px-3">
            {{ stop.FechaPartida.slice(11, 16) }}
            </div>

            <!-- Info -->
            <div class="mt-1">
            <div class="text-xs uppercase text-blue-500 tracking-wider font-semibold leading-tight">
                {{ stop.LocalidadNombre }}
            </div>
            <div class="text-base font-bold text-gray-900 leading-tight">
                {{ stop.TerminalNombre }}
            </div>
            <div class="mt-1">
                <a href="https://www.google.com/maps/search/{{ stop.TerminalNombre }}"
                target="_blank"
                class="text-sm text-blue-600 hover:underline">
                Mapa ↗
                </a>
            </div>

            <!--div *ngIf="i < stops.length - 1" class="text-xs text-gray-500 mt-1 pl-1">
                ↓ {{ calcularDuracion(stop.FechaPartida, stops[i + 1].FechaArribo) }}
            </div-->
            </div>
        </div>
        </div>
      </div>

      <div class="text-right mt-6">
        <button mat-raised-button color="primary" (click)="dialogRef.close()">Cerrar</button>
      </div>
    </div>
  `,
    styles: [`
        @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        .animate-fade-in {
            animation: fade-in 0.3s ease-out;
        }`
    ]
})
export class RecorridoComponent {
    @Input() alias!: string;
    @Input() id_viaje!: string;
    @Input() origen!: string;
    @Input() destino!: string;
    stops: any[] = [];
    loading = false;
    error = false;
    origenIndex = -1;
    destinoIndex = -1;
    // Detectamos si se ejecuta como modal o no
    get enDialog(): boolean {
        return !!this.dialogData;
    }

    providersDisponibles = ['la20', 'suma', 'crucerodelnorte', 'cometabis', 'iselin', 'raybus', 'crucerodelsur'];

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
        @Optional() public dialogRef: MatDialogRef<RecorridoComponent>
    ) {
        if (this.enDialog) {
            // Si se abre como modal, usamos los datos del dialog
            this.alias = dialogData.alias;
            this.id_viaje = dialogData.id_viaje;
            this.origen = dialogData.origen;
            this.destino = dialogData.destino;
            this.fetchRoute();
        }
    }

    abrirDialog() {
        this.dialog.open(RecorridoComponent, {
            data: { alias: this.alias, id_viaje: this.id_viaje, origen: this.origen, destino: this.destino },
            width: '600px',
            maxHeight: '80vh'
        });
    }

    fetchRoute() {
        this.loading = true;
        this.error = false;

        if(!this.providersDisponibles.includes(this.alias)) {
            this.error = true;
            this.loading = false;
            return;
        }

        const url = environment.providersApiUrl + `/services/${this.alias}/route?id_viaje=${this.id_viaje}`;
        this.http.get<any>(url).subscribe({
            next: res => {
                if(res?.recorrido?.parada == undefined) {
                    this.error = true;
                    this.loading = false;
                    return;
                }
                const paradas = res?.recorrido?.parada || [];
                this.stops = paradas.map((p: any) => p['@attributes']);
                this.stops.sort((a, b) => Number(a.Numero_Orden) - Number(b.Numero_Orden));
                this.origenIndex = this.stops.findIndex(stop => stop.TerminalID == this.origen);
                this.destinoIndex = this.stops.findIndex(stop => stop.TerminalID == this.destino);
                this.loading = false;
            },
            error: () => {
                this.error = true;
                this.loading = false;
            }
        });
    }

    calcularDuracion(salida: string, llegada: string): string {
        const salidaDate = new Date(salida);
        const llegadaDate = new Date(llegada);
        const diffMs = llegadaDate.getTime() - salidaDate.getTime();
        const horas = Math.floor(diffMs / (1000 * 60 * 60));
        const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if(horas == undefined || minutos == undefined) {
            return '0h 0m';
        }
        return `${horas}h ${minutos}m`;
    }

    getInitialColor(id: string): string {
        if (id == this.origen) {
            return 'bg-blue-600 border-blue-600';
        }
        if (id == this.destino) {
            return 'bg-green-600 border-green-600';
        }
        return 'bg-gray-300 border-gray-300';
    }

    getColor(index: number, terminalId: string): string {
        if (terminalId == this.origen) return 'bg-blue-600 border-blue-600';   // Origen
        if (terminalId == this.destino) return 'bg-green-600 border-green-600'; // Destino

        if (
          this.origenIndex !== -1 &&
          this.destinoIndex !== -1 &&
          index > this.origenIndex &&
          index < this.destinoIndex
        ) {
          return 'bg-orange-400 border-orange-400'; // Intermedio dentro del viaje
        }
      
        return 'bg-gray-300 border-gray-300'; // Fuera del tramo de viaje
      }


}
